<script setup lang="ts">
export type IconAnimation = 'float' | 'pulse' | 'float-pulse' | 'semicircle' | 'scale' | 'shake' | 'spin' | 'none'
export type AnimationTrigger = 'always' | 'hover'

withDefaults(defineProps<{
  animation?: IconAnimation
  trigger?: AnimationTrigger
  size?: number
  color?: string
  delay?: number
}>(), {
  animation: 'float',
  trigger: 'always',
  size: 24,
  delay: 0
})
</script>

<template>
  <div 
    class="animated-icon" 
    :class="[`anim-${animation}`, `trigger-${trigger}`]" 
    :style="{ color }"
  >
    <slot />
  </div>
</template>

<style scoped>
.animated-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.animated-icon :deep(svg) {
  width: v-bind('size + "px"');
  height: v-bind('size + "px"');
  transition: transform calc(0.3s * var(--motion-scale)) ease;
}

/* Animations */
@keyframes iconFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes iconSemicircle {
  0% { transform: rotate(0deg); }
  50% { transform: rotate(180deg); }
  100% { transform: rotate(0deg); }
}

@keyframes iconShake {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
}

@keyframes iconSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Trigger: Always */
.animated-icon.trigger-always.anim-float :deep(svg) {
  animation: iconFloat 3s ease-in-out v-bind('delay + "s"') infinite;
}

.animated-icon.trigger-always.anim-pulse :deep(svg) {
  animation: iconPulse 2s ease-in-out v-bind('delay + "s"') infinite;
}

.animated-icon.trigger-always.anim-float-pulse :deep(svg) {
  animation: iconFloat 3s ease-in-out v-bind('delay + "s"') infinite, iconPulse 2s ease-in-out v-bind('delay + "s"') infinite;
}

.animated-icon.trigger-always.anim-semicircle :deep(svg) {
  animation: iconSemicircle 2s ease-in-out v-bind('delay + "s"') infinite;
}

.animated-icon.trigger-always.anim-spin :deep(svg) {
  animation: iconSpin 1s linear v-bind('delay + "s"') infinite;
}

/* Trigger: Hover */
/* Standard Hover: Scale (Zoom) */
.animated-icon.trigger-hover.anim-scale:hover :deep(svg) {
  transform: scale(1.15);
}

/* Standard Hover: Shake (often used for delete) */
.animated-icon.trigger-hover.anim-shake:hover :deep(svg) {
  animation: iconShake 0.4s ease-in-out;
}

/* Standard Hover: Spin (Refresh) */
.animated-icon.trigger-hover.anim-spin:hover :deep(svg) {
  animation: iconSpin 1s linear infinite;
}

/* Previously used animations on hover */
.animated-icon.trigger-hover:hover.anim-float :deep(svg) {
  animation: iconFloat 3s ease-in-out infinite;
}

.animated-icon.trigger-hover:hover.anim-pulse :deep(svg) {
  animation: iconPulse 2s ease-in-out infinite;
}

.animated-icon.trigger-hover:hover.anim-float-pulse :deep(svg) {
  animation: iconFloat 3s ease-in-out infinite, iconPulse 2s ease-in-out infinite;
}

.animated-icon.trigger-hover.anim-semicircle:hover :deep(svg) {
  transform: rotate(180deg);
}
</style>
